import ArticleLayout from '../../components/ArticleLayout';
import LuftlommeLydFigure from '../../components/figures/LuftlommeLydFigure';

export default function RadiatorLyderSomVand() {
  return (
    <ArticleLayout
      title="Radiatoren lyder som vand: Rislen, plasken og vandfaldslyde"
      metaTitle="Radiator lyder som vand? Sådan fjerner du rislelyden"
      metaDescription="Lyder radiatoren som en dryppende hane, rislende bæk eller et vandfald? Læs hvorfor luftbobler skaber lyden, og hvordan du lufter anlægget helt ud."
      canonicalPath="/radiator-lyder-som-vand"
      introLead="Når en radiator lyder som en rislende bæk, dryppende vand eller et lille indvendigt vandfald, er diagnosen entydig: Der er luft i radiatorkroppen."
      introAnswer="I et velfungerende radiatorsystem er radiatorkroppen 100 % fyldt med vand under tryk. Når der slipper luft ind i systemet, samler luften sig øverst, og det indstrømmende vand styrter frit ned igennem hulrummet. Løsningen er en grundig udluftning med slukket cirkulationspumpe."
      tableOfContents={[
        { id: 'tjekliste', title: '1. Tjekliste: Sådan stopper du vandlydene' },
        { id: 'hvorfor-lyd', title: '2. Hvorfor plasker og risler vandet?' },
        { id: 'pumpe-trick', title: '3. Sluk cirkulationspumpen under udluftning' },
        { id: 'luft-kilde', title: '4. Hvor kommer luften overhovedet fra?' },
        { id: 'faq', title: '5. Ofte stillede spørgsmål' },
      ]}
      checklistTitle="Tjekliste: 5 trin til at fjerne vandlyde fra radiatoren"
      checklist={[
        {
          title: 'Sluk for cirkulationspumpen ved varmeanlægget',
          description: 'Når pumpen slukkes i 15–30 minutter, falder vandet til ro, og mikroskopiske luftbobler stiger op til luftskruen.',
        },
        {
          title: 'Åbn termostaten helt op på radiatoren',
          description: 'Sæt termostathovedet på trin 5, så ventilen åbner fuldstændigt op for det indre kammer.',
        },
        {
          title: 'Hold en klud og en beholder under luftskruen',
          description: 'Placér en radiatornøgle på den firkantede tap på luftskruen i toppen af den modsatte ende.',
        },
        {
          title: 'Drej luftskruen en halv omgang mod uret',
          description: 'Lyt efter den hvislende luft, og hold skruen åben, indtil der løber en uafbrudt, rolig stråle vand ud.',
        },
        {
          title: 'Genstart pumpen og kontrollér trykket på manometeret',
          description: 'Start cirkulationspumpen igen, og kontrollér at anlægstrykket stadig er omkring 1,5 bar i et parcelhus.',
        },
      ]}
      diagram={<LuftlommeLydFigure />}
      sections={[
        {
          id: 'hvorfor-lyd',
          title: 'Hvorfor opstår plaske- og vandfaldslydene?',
          content: (
            <>
              <p>
                En radiator er designet til at arbejde helt lydløst. Når systemet er fyldt, er vandet ukomprimerbart, og der er ingen frie væskeoverflader.
              </p>
              <p>
                Når der er luft til stede, dannes der et tomt kammer i toppen. Hver gang termostatventilen åbner for vandtilførslen, sprøjter det varme vand ind og plasker ned over radiatorens indvendige lameller mod vandspejlet længere nede. Det skaber den velkendte lyd af rislende vand eller en dryppende hane inde bag stålpanelet.
              </p>
            </>
          ),
        },
        {
          id: 'pumpe-trick',
          title: 'Det afgørende trick: Sluk cirkulationspumpen',
          content: (
            <>
              <p>
                Mange boligejere lufter ud, mens cirkulationspumpen kører med fuld hastighed. Det er ofte ineffektivt: Pumpen pisker luften rundt i vandstrømmen som millioner af mikrobobler, så luften aldrig når at samle sig ved luftskruen.
              </p>
              <p>
                Sluk for pumpen på kontakten i bryggerset eller kælderen, og vent 20 minutter. Først når vandet står helt stille, samler alle bobler sig til én stor luftlomme øverst i anlæggets højest placerede radiatorer, hvorefter de kan lukkes ud med et enkelt snuptag.
              </p>
            </>
          ),
        },
        {
          id: 'luft-kilde',
          title: 'Hvor kommer luften fra, og hvorfor vender den tilbage?',
          content: (
            <>
              <p>
                Vender rislelyden tilbage få uger efter udluftning, slipper der udefrakommende luft ind i anlægget. Det sker typisk af tre årsager:
              </p>
              <p>
                <strong>1. For lavt anlægstryk:</strong> Hvis vandtrykket falder under det statiske tryk i boligens højde, opstår der undertryk i de øverste radiatorer. Pakdåser og samlinger, der er tætte mod vandudslip, kan suge atmosfærisk luft ind ved undertryk.
              </p>
              <p>
                <strong>2. Defekt trykekspansionsbeholder:</strong> Hvis gummimembranen i ekspansionsbeholderen er punkteret, kan anlægget ikke optage vandets varmeudvidelse. Trykket svinger voldsomt, og sikkerhedsventilen smider vand ud.
              </p>
              <p>
                <strong>3. Iltning fra frisk påfyldningsvand:</strong> Hver gang du efterfylder med koldt postevand, tilføres opløst ilt, som frigives ved opvarmning.
              </p>
            </>
          ),
        },
      ]}
      proCallout={{
        title: 'Hvornår skal VVS-fagmanden undersøge anlægget?',
        content: (
          <p>
            Hvis du må udlufte radiatoren gentagne gange hver måned, eller hvis vandet der kommer ud er kulsort og lugter kraftigt af rådne æg (svovlbrinte/gasdannelse fra korrosion), bør anlægget gennemgås af en autoriseret VVS-installatør for defekt ekspansionsbeholder eller skjulte utætheder.
          </p>
        ),
      }}
      faq={[
        {
          q: 'Er rislende vand i radiatoren tegn på en vandskade?',
          a: 'Nej, rislelyden foregår inde i det lukkede rørsystem og betyder ikke, at der løber vand ud på gulvet. Det betyder blot, at der er luft til stede inde i radiatoren.',
        },
        {
          q: 'Hvorfor er radiatoren kold i toppen, når den risler?',
          a: 'Luft er lettere end vand og samler sig altid øverst. Luften blokerer for, at det varme vand kan fylde den øverste del af radiatoren, hvorfor toppen bliver kold.',
        },
        {
          q: 'Hvor ofte skal man udlufte sine radiatorer?',
          a: 'Normalt bør det kun være nødvendigt én gang om året ved fyringssæsonens start i oktober. Hyppigere behov er tegn på trykfald eller defekt ekspansionsbeholder.',
        },
      ]}
      internalLinks={[
        { to: '/udluftning-af-radiator', text: 'Udluftning af radiator', desc: 'komplet guide til korrekt rækkefølge' },
        { to: '/radiator-larmer', text: 'Radiatoren larmer, suser eller banker', desc: 'andre typer af radiatorstøj' },
        { to: '/radiator-tryk', text: 'Trykket i varmeanlægget', desc: 'sådan kontrollerer og efterfylder du tryk' },
        { to: '/radiator-luftskrue-sidder-fast', text: 'Luftskruen sidder fast', desc: 'hvad du gør, hvis skruen ikke kan åbnes' },
      ]}
      sources={[
        { url: 'https://sparenergi.dk/privat/spar-energi-i-hverdagen/saadan-faar-du-mest-ud-af-fjernvarmen', label: 'Sådan får du mest ud af fjernvarme | SparEnergi' },
        { url: 'https://sparenergi.dk/privat/spar-energi-i-hverdagen/spar-paa-varme-og-vand-i-hjemmet', label: 'Normalt varmeforbrug i kWh (+ fjernvarme i MWh) | SparEnergi' },
      ]}
    />
  );
}
